/**
 * Sidebar (plan 6.B): back link, event summary, section nav, tip.
 * Narrow/phone: only the active row is visible (CSS); tapping it toggles a dropdown with every
 * section (5.4 "Mobile navigation gap").
 */

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import octoIcon from "../../../../assets/icons/octo_icon.svg";
import Icon from "../ui/Icon";
import { useBuilder } from "../state/builderContext";
import { actions } from "../state/builderReducer";
import { COPY, ROUTES, SECTIONS } from "../state/constants";
import { eventDisplayName, sectionsWithErrors, summaryLine, visibleSections } from "../state/selectors";

const SECTION_ICON = {
  [SECTIONS.QUICK]: "sparkle",
  [SECTIONS.AVAILABILITY]: "calendar",
  [SECTIONS.HOSTS]: "users",
  [SECTIONS.INVITEE]: "message",
  [SECTIONS.NOTIFICATIONS]: "bell",
  [SECTIONS.PAYMENTS]: "card",
};

const NavButton = ({ section, active, error, ready, onClick, extraClass = "" }) => (
  <button type="button" className={`${active ? "active" : ""} ${extraClass}`.trim()} onClick={onClick} aria-current={active ? "page" : undefined}>
    <Icon name={SECTION_ICON[section]} size={19} />
    <span>{COPY.sections[section]}</span>
    {error ? <i className="eb-error-dot" aria-label="Needs attention" /> : ready ? <i className="eb-ready-dot" /> : <i />}
    <Icon name="chevron" size={15} />
  </button>
);

const BuilderSidebar = ({ bp }) => {
  const { state, dispatch, goToSection, issues, confirm, closeConfirm, flush } = useBuilder();
  const navigate = useNavigate();
  const isNarrow = bp === "narrow" || bp === "phone";
  const errorSections = sectionsWithErrors(state, issues);
  const sections = visibleSections(state);
  const active = state.meta.section;
  const navOpen = state.ui.navOpen && isNarrow;
  const anchorRef = useRef(null);

  // close the mobile dropdown on outside click
  useEffect(() => {
    if (!navOpen) return undefined;
    const onDoc = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) dispatch(actions.ui({ navOpen: false }));
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [navOpen, dispatch]);

  const leave = () => {
    const go = () => navigate(ROUTES.PICKER);
    if (state.save.status === "saving") {
      confirm({
        title: "Changes are still saving",
        body: "Leave now and the last edit may not be saved.",
        confirmLabel: "Wait and save",
        cancelLabel: "Leave anyway",
        onConfirm: async () => {
          closeConfirm();
          await flush();
          go();
        },
        onCancel: () => {
          closeConfirm();
          go();
        },
      });
      return;
    }
    go();
  };

  const rowClick = (section) => {
    if (isNarrow && section === active) {
      dispatch(actions.ui({ navOpen: !state.ui.navOpen }));
      return;
    }
    goToSection(section);
  };

  const quickReady = !errorSections.has(SECTIONS.QUICK) && !issues.some((i) => i.section === SECTIONS.QUICK);

  return (
    <aside className="eb-builder-sidebar">
      <button type="button" className="eb-back-events" onClick={leave}>
        <Icon name="arrow-left" size={19} />
        {COPY.sidebar.back}
      </button>

      <div className="eb-side-event-summary">
        <div className="eb-mini-octo">
          <img src={octoIcon} alt="" />
        </div>
        <div>
          <strong>{eventDisplayName(state)}</strong>
          <span>{summaryLine(state)}</span>
        </div>
      </div>

      <nav aria-label="Event settings" className="eb-nav-anchor" ref={anchorRef}>
        <NavButton
          section={SECTIONS.QUICK}
          active={active === SECTIONS.QUICK}
          error={errorSections.has(SECTIONS.QUICK)}
          ready={quickReady}
          onClick={() => rowClick(SECTIONS.QUICK)}
        />
        <p>{COPY.sidebar.additional}</p>
        {sections
          .filter((s) => s !== SECTIONS.QUICK)
          .map((s) => (
            <NavButton key={s} section={s} active={active === s} error={errorSections.has(s)} ready={false} onClick={() => rowClick(s)} />
          ))}
        {navOpen ? (
          <div className="eb-nav-dropdown" role="menu">
            {sections.map((s) => (
              <NavButton
                key={`dd-${s}`}
                section={s}
                active={false}
                error={errorSections.has(s)}
                ready={s === SECTIONS.QUICK && quickReady}
                onClick={() => goToSection(s)}
              />
            ))}
          </div>
        ) : null}
      </nav>

      <div className="eb-sidebar-tip">
        <Icon name="sparkle" size={19} />
        <div>
          <strong>{COPY.sidebar.tipTitle}</strong>
          <p>{COPY.sidebar.tipBody}</p>
        </div>
      </div>
    </aside>
  );
};

export default BuilderSidebar;
